import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import WelcomeGuide from '../components/WelcomeGuide';
import api from '../api/client';
import './Dashboard.css';

const fmtINR = v => '₹' + (Number(v) || 0).toLocaleString('en-IN');
const fmtDateShort = (d) => { try { return new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short'}); } catch { return d; } };

function Spark({ data, color }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data), min = Math.min(...data);
  const W = 80, H = 28, P = 2;
  const pts = data.map((v,i) => ({
    x: P + (i/Math.max(data.length-1,1))*(W-P*2),
    y: P + (1-(v-min)/(max-min||1))*(H-P*2)
  }));
  const d = pts.map((p,i) => `${i===0?'M':'L'}${p.x},${p.y}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:W,height:H,display:'block'}}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/>
    </svg>
  );
}

function RevenueChart({ data }) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun'];
  const max = Math.max(...data);
  const W = 520, H = 120, PB = 22, PT = 10, PL = 4, PR = 4;
  const pts = data.map((v,i) => ({
    x: PL + (i/(data.length-1))*(W-PL-PR),
    y: PT + (1-v/max)*(H-PT-PB)
  }));
  const pathD = pts.map((p,i) => `${i===0?'M':'L'}${p.x},${p.y}`).join(' ');
  const areaD = `${pathD} L${pts[pts.length-1].x},${H-PB} L${pts[0].x},${H-PB} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:H,display:'block',overflow:'visible'}}>
      <defs>
        <linearGradient id="dbRevGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F5A623" stopOpacity="0.25"/>
          <stop offset="100%" stopColor="#F5A623" stopOpacity="0"/>
        </linearGradient>
      </defs>
      {[0,1,2].map(i => {
        const y = PT + i*(H-PT-PB)/2;
        return <line key={i} x1={PL} y1={y} x2={W-PR} y2={y} stroke="currentColor" strokeOpacity="0.05" strokeWidth="0.5"/>;
      })}
      <path d={areaD} fill="url(#dbRevGrad)"/>
      <path d={pathD} fill="none" stroke="#F5A623" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      {pts.map((p,i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3.5" fill="#F5A623"/>
          <circle cx={p.x} cy={p.y} r="1.5" fill="#fff"/>
          <text x={p.x} y={H-5} textAnchor="middle" fontSize="8.5" fill="currentColor" opacity="0.35">{months[i]}</text>
        </g>
      ))}
    </svg>
  );
}

const KPI_DETAILS = {
  revenue: { title:'Revenue this month', color:'#F5A623', rows:[{label:'Collected',val:'₹1.8L',note:'2 paid'},{label:'Pending',val:'₹2.4L',note:'4 invoices'},{label:'Overdue',val:'₹35K',note:'1 invoice'},{label:'Target',val:'₹3L',note:'80% done'}], note:'View Finance →', path:'/finance' },
  leads:   { title:'Active leads', color:'#22C55E', rows:[{label:'New',val:'12',note:''},{label:'Contacted',val:'9',note:''},{label:'Warm',val:'10',note:''},{label:'Hot',val:'7',note:'follow up now'},{label:'Closed',val:'6',note:'this month'},{label:'Lost',val:'4',note:''}], note:'View Leads →', path:'/leads' },
  tasks:   { title:'Tasks done', color:'#3B82F6', rows:[{label:'Completed',val:'47',note:''},{label:'Overdue',val:'2',note:'action needed'},{label:'In progress',val:'8',note:''},{label:'Not started',val:'3',note:''}], note:'View Projects →', path:'/projects' },
  team:    { title:'Team present', color:'#8B5CF6', rows:[{label:'Present today',val:'9',note:''},{label:'On leave',val:'3',note:'approved'},{label:'Absent',val:'0',note:''},{label:'Total members',val:'12',note:''}], note:'View Team →', path:'/team' },
};

function KPIModal({ data, theme, onClose, navigate }) {
  return (
    <div className="db-kpi-overlay" onClick={onClose}>
      <div className="db-kpi-modal" data-theme={theme} onClick={e=>e.stopPropagation()}>
        <div className="db-kpi-hd">
          <div className="db-kpi-title" style={{color:data.color}}>{data.title}</div>
          <div className="db-kpi-close" onClick={onClose}>✕</div>
        </div>
        <div className="db-kpi-rows">
          {data.rows.map(r=>(
            <div key={r.label} className="db-kpi-row">
              <span className="db-kpi-row-lbl">{r.label}</span>
              <div className="db-kpi-row-right">
                <span className="db-kpi-row-val">{r.val}</span>
                {r.note && <span className="db-kpi-row-note">{r.note}</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="db-kpi-link" onClick={()=>{navigate(data.path);onClose();}}>{data.note}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { theme } = useTheme();
  const navigate  = useNavigate();
  const today     = new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
  const [kpiModal, setKpiModal] = useState(null);
  const [showFollowUps, setShowFollowUps] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];

  // ── Real data from the backend API ──
  const [allLeads, setAllLeads] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allTeam, setAllTeam] = useState([]);

  React.useEffect(() => {
    api.leads.list().then(rows => setAllLeads(rows.map(l => ({...l, followUp: l.follow_up_date})))).catch(()=>{});
    api.clients.list().then(setAllClients).catch(()=>{});
    api.finance.invoices.list().then(rows => setAllInvoices(rows.map(i => ({...i, client: i.client_name, date: i.invoice_date, due: i.due_date, amount: parseFloat(i.amount)||0})))).catch(()=>{});
    api.projects.list().then(rows => setAllProjects(rows.map(p => ({...p, title: p.title, due: p.due_date})))).catch(()=>{});
    api.team.list().then(setAllTeam).catch(()=>{});
  }, []);

  const userName = (() => { try { return JSON.parse(sessionStorage.getItem('ryzenos_session'))?.name || 'there'; } catch { return 'there'; } })();
  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; })();

  const dueTodayLeads = allLeads.filter(l => l.followUp === todayStr);
  const overdueLeads = allLeads.filter(l => l.followUp && l.followUp < todayStr);
  const closedLeads = allLeads.filter(l => l.status === 'won' || l.status === 'closed');
  const totalDue = dueTodayLeads.length + overdueLeads.length;

  // Revenue this month
  const curMonth = new Date().getMonth();
  const curYear = new Date().getFullYear();
  const paidInvoicesThisMonth = allInvoices.filter(i => {
    if (i.status !== 'paid' || !i.date) return false;
    const d = new Date(i.date);
    return d.getMonth() === curMonth && d.getFullYear() === curYear;
  });
  const revenueThisMonth = paidInvoicesThisMonth.reduce((s,i)=>s+(i.amount||0),0);
  const pendingAmt = allInvoices.filter(i=>i.status==='pending').reduce((s,i)=>s+(i.amount||0),0);
  const overdueAmt = allInvoices.filter(i=>i.status==='overdue').reduce((s,i)=>s+(i.amount||0),0);

  // Last 6 months revenue for chart
  const revenueData = Array.from({length:6}).map((_,idx) => {
    const m = (curMonth - 5 + idx + 12) % 12;
    const y = curMonth - 5 + idx < 0 ? curYear - 1 : curYear;
    return allInvoices.filter(i => {
      if (i.status !== 'paid' || !i.date) return false;
      const d = new Date(i.date);
      return d.getMonth() === m && d.getFullYear() === y;
    }).reduce((s,i)=>s+(i.amount||0),0);
  });
  const hasRevenueData = revenueData.some(v=>v>0);

  // Tasks
  const doneTasks = allProjects.filter(p=>p.status==='done').length;
  const totalTasks = allProjects.length;
  const taskPct = totalTasks ? Math.round((doneTasks/totalTasks)*100) : 0;
  const overdueTasks = allProjects.filter(p => p.due && p.due < todayStr && p.status !== 'done').length;

  // Team
  const presentCount = allTeam.filter(m=>m.status==='present').length;
  const onLeaveCount = allTeam.filter(m=>m.status==='leave').length;
  const absentCount = allTeam.filter(m=>m.status==='absent').length;
  const totalTeam = allTeam.length;

  const STAT_CARDS = [
    { key:'revenue', lbl:'Revenue this month', val:fmtINR(revenueThisMonth),  chg:paidInvoicesThisMonth.length+' invoices paid', cls:'db-up',   color:'#F5A623', spark: hasRevenueData?revenueData:[0,0,0,0,0,0] },
    { key:'leads',   lbl:'Active leads',       val:String(allLeads.length),  chg:allLeads.filter(l=>l.status==='new').length+' new',  cls:'db-up',   color:'#22C55E', spark:[0,0,0,0,0,allLeads.length] },
    { key:'tasks',   lbl:'Tasks done',         val:totalTasks?taskPct+'%':'—',  chg:overdueTasks+' overdue',          cls:'db-warn', color:'#3B82F6', spark:[0,0,0,0,0,taskPct] },
    { key:'team',    lbl:'Team present',       val:totalTeam?presentCount+'/'+totalTeam:'—',   chg:onLeaveCount+' on leave',         cls:'db-dn',   color:'#8B5CF6', spark:[0,0,0,0,0,presentCount] },
  ];

  const KPI_DETAILS = {
    revenue: { title:'Revenue this month', color:'#F5A623', rows:[
      {label:'Collected', val:fmtINR(revenueThisMonth), note:paidInvoicesThisMonth.length+' paid'},
      {label:'Pending', val:fmtINR(pendingAmt), note:allInvoices.filter(i=>i.status==='pending').length+' invoices'},
      {label:'Overdue', val:fmtINR(overdueAmt), note:allInvoices.filter(i=>i.status==='overdue').length+' invoice'},
    ], note:'View Finance →', path:'/finance' },
    leads: { title:'Active leads', color:'#22C55E', rows:
      ['new','contacted','meeting-done','proposal-sent','negotiation','won','lost'].map(s => ({
        label: s.split('-').map(w=>w[0].toUpperCase()+w.slice(1)).join(' '),
        val: String(allLeads.filter(l=>l.status===s).length), note:''
      })),
      note:'View Leads →', path:'/leads' },
    tasks: { title:'Tasks done', color:'#3B82F6', rows:[
      {label:'Completed', val:String(doneTasks), note:''},
      {label:'Overdue', val:String(overdueTasks), note:overdueTasks?'action needed':''},
      {label:'In progress', val:String(allProjects.filter(p=>p.status==='inprogress').length), note:''},
      {label:'To do', val:String(allProjects.filter(p=>p.status==='todo').length), note:''},
    ], note:'View Projects →', path:'/projects' },
    team: { title:'Team present', color:'#8B5CF6', rows:[
      {label:'Present today', val:String(presentCount), note:''},
      {label:'On leave', val:String(onLeaveCount), note:''},
      {label:'Absent', val:String(absentCount), note:''},
      {label:'Total members', val:String(totalTeam), note:''},
    ], note:'View Team →', path:'/team' },
  };

  // Lead sources
  const sourceCounts = {};
  allLeads.forEach(l => { const s = l.source || 'Other'; sourceCounts[s] = (sourceCounts[s]||0)+1; });
  const totalLeadsForSource = allLeads.length || 1;
  const sourceColors = ['#F5A623','#3B82F6','#22C55E','#8B5CF6','#6B7280'];
  const LEAD_SOURCES = Object.entries(sourceCounts).map(([label,count],idx) => ({
    label, val: Math.round((count/totalLeadsForSource)*100), color: sourceColors[idx % sourceColors.length]
  })).sort((a,b)=>b.val-a.val);
  const maxSource = Math.max(1, ...LEAD_SOURCES.map(s=>s.val));

  // KPI row 4
  const lastMonthIdx = (curMonth - 1 + 12) % 12;
  const lastMonthYear = curMonth === 0 ? curYear - 1 : curYear;
  const lastMonthRev = allInvoices.filter(i => {
    if (i.status !== 'paid' || !i.date) return false;
    const d = new Date(i.date);
    return d.getMonth() === lastMonthIdx && d.getFullYear() === lastMonthYear;
  }).reduce((s,i)=>s+(i.amount||0),0);

  const KPI4 = [
    { lbl:'Total Clients',   val: allClients.length,    sub:'across all ventures',  color:'#F5A623', path:'/clients'},
    { lbl:'Total Projects',  val: allProjects.length,   sub:allProjects.filter(p=>p.status!=='done').length+' active', color:'#3B82F6', path:'/projects'},
    { lbl:'Open Leads',      val: allLeads.filter(l=>l.status!=='won'&&l.status!=='lost').length, sub:'in pipeline', color:'#22C55E', path:'/leads'},
    { lbl:'Last Month Rev',  val: fmtINR(lastMonthRev), sub:new Date(lastMonthYear,lastMonthIdx).toLocaleDateString('en-IN',{month:'long',year:'numeric'}), color:'#8B5CF6', path:'/finance'},
  ];

  // Projects panel
  const PROJECTS = allProjects.filter(p=>p.status!=='done').slice(0,5).map(p => ({
    name: p.title,
    status: p.status==='todo'?'To Do':p.status==='inprogress'?'In Progress':'In Review',
    progress: p.status==='todo'?10:p.status==='inprogress'?55:85,
    due: p.due ? fmtDateShort(p.due) : 'No deadline',
    color: p.status==='inprogress'?'#3B82F6':p.status==='review'?'#F5A623':'#6B7280',
  }));
  const allProjectsOnTrack = allProjects.filter(p=>p.due && p.due < todayStr && p.status !== 'done').length === 0;

  // Top clients by value (from invoices)
  const clientTotals = {};
  allInvoices.forEach(i => { if (i.client) clientTotals[i.client] = (clientTotals[i.client]||0) + (i.amount||0); });
  const TOP_CLIENTS = Object.entries(clientTotals).map(([name,val])=>({name,val})).sort((a,b)=>b.val-a.val).slice(0,5);
  const maxClient = Math.max(1, ...TOP_CLIENTS.map(c=>c.val));

  // Recent leads
  const LEADS = [...allLeads].sort((a,b)=>(b.id||0)-(a.id||0)).slice(0,5).map(l => ({
    name: l.name, src: l.source || '—',
    badge: l.status==='new'?'New':l.status==='won'?'Won':l.status==='negotiation'?'Negotiation':l.status==='contacted'?'Contacted':l.status,
    cls: l.status==='new'?'db-b-green':l.status==='won'?'db-b-gold':'db-b-blue',
  }));

  // AI insights -- generated from real conditions, hidden if nothing to say
  const AI_INSIGHTS = [];
  if (overdueLeads.length > 0) AI_INSIGHTS.push(<><strong>{overdueLeads.length} lead{overdueLeads.length>1?'s':''}</strong> overdue for follow-up.</>);
  if (revenueThisMonth > 0) AI_INSIGHTS.push(<>Revenue this month: <strong>{fmtINR(revenueThisMonth)}</strong>.</>);
  if (absentCount > 0) AI_INSIGHTS.push(<><strong>{absentCount} employee{absentCount>1?'s':''}</strong> absent today.</>);
  if (overdueTasks > 0) AI_INSIGHTS.push(<><strong>{overdueTasks} task{overdueTasks>1?'s':''}</strong> overdue.</>);
  if (AI_INSIGHTS.length === 0) AI_INSIGHTS.push(<>No alerts right now — everything looks on track.</>);

  return (
    <div className="db-wrap" data-theme={theme}>
      <WelcomeGuide/>
      <Sidebar/>
      <div className="db-main">
        <Topbar title={<>{greeting}, <span>{userName}</span></>}/>
        <div className="db-content">

          {/* Today strip */}
          <div className="db-today" onClick={()=>setShowFollowUps(true)} style={{cursor:'pointer'}}>
            <div><div className="db-today-sub">{today}</div><div className="db-today-title">{totalDue > 0 ? `${totalDue} follow-up${totalDue>1?'s':''} due` : 'No follow-ups due today'}</div></div>
            <div className="db-chips">
              {dueTodayLeads.length > 0 && <div className="db-chip db-chip-gold">{dueTodayLeads.length} today</div>}
              {closedLeads.length > 0 && <div className="db-chip db-chip-green">{closedLeads.length} closed</div>}
              {overdueLeads.length > 0 && <div className="db-chip db-chip-red">{overdueLeads.length} overdue</div>}
              {totalDue === 0 && <div className="db-chip db-chip-green">All clear</div>}
            </div>
          </div>

          {showFollowUps && (
            <div className="db-kpi-overlay" onClick={()=>setShowFollowUps(false)}>
              <div className="db-kpi-modal" data-theme={theme} onClick={e=>e.stopPropagation()} style={{width:420}}>
                <div className="db-kpi-hd">
                  <div className="db-kpi-title" style={{color:'#F5A623'}}>Follow-ups due today</div>
                  <div className="db-kpi-close" onClick={()=>setShowFollowUps(false)}>✕</div>
                </div>
                <div className="db-kpi-rows" style={{maxHeight:360,overflowY:'auto'}}>
                  {dueTodayLeads.length === 0 ? (
                    <div className="db-empty-state" style={{padding:'16px 0',textAlign:'center',fontSize:13}}>No follow-ups due today</div>
                  ) : dueTodayLeads.map(l=>(
                    <div key={l.id} className="db-kpi-row">
                      <div>
                        <div style={{fontSize:13,fontWeight:600}}>{l.name}</div>
                        <div style={{fontSize:11,opacity:.5,marginTop:2}}>{l.source} · {l.status}</div>
                      </div>
                      <div className="db-kpi-row-right">
                        {l.value>0 && <span className="db-kpi-row-val">{fmtINR(l.value)}</span>}
                        <span className="db-kpi-row-note">{l.assigned}</span>
                      </div>
                    </div>
                  ))}
                  {overdueLeads.length > 0 && <>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',opacity:.4,padding:'12px 0 4px'}}>Overdue</div>
                    {overdueLeads.map(l=>(
                      <div key={l.id} className="db-kpi-row">
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:'#ef4444'}}>{l.name}</div>
                          <div style={{fontSize:11,opacity:.5,marginTop:2}}>{l.followUp} · {l.source}</div>
                        </div>
                        <span className="db-kpi-row-note" style={{color:'#ef4444'}}>{l.assigned}</span>
                      </div>
                    ))}
                  </>}
                </div>
                <div className="db-kpi-link" onClick={()=>{navigate('/leads');setShowFollowUps(false);}}>View all leads →</div>
              </div>
            </div>
          )}

          {/* Stat cards */}
          <div className="db-stats">
            {STAT_CARDS.map(s => (
              <div key={s.key} className="db-stat db-stat-clickable" onClick={()=>setKpiModal(s.key)}>
                <div className="db-stat-lbl">{s.lbl}</div>
                <div className="db-stat-val" style={s.key==='revenue'?{color:'#F5A623'}:{}}>{s.val}</div>
                <div className={`db-stat-chg ${s.cls}`}>{s.chg}</div>
                <Spark data={s.spark} color={s.color}/>
              </div>
            ))}
          </div>

          {/* Revenue chart + Lead sources */}
          <div className="db-chart-row">
            <div className="db-panel db-panel-wide">
              <div className="db-panel-hd">
                <span className="db-panel-title">Revenue trend — last 6 months</span>
                <span className="db-panel-tag" style={{color:'#F5A623'}}>This month: {fmtINR(revenueThisMonth)}</span>
              </div>
              {hasRevenueData ? <RevenueChart data={revenueData}/> : <div className="db-empty-state" style={{padding:'30px 0',textAlign:'center',fontSize:12}}>No revenue recorded yet</div>}
            </div>
            <div className="db-panel">
              <div className="db-panel-hd"><span className="db-panel-title">Lead sources</span></div>
              <div className="db-source-list">
                {LEAD_SOURCES.length === 0 ? <div className="db-empty-state" style={{padding:'12px 0',fontSize:12}}>No leads yet</div> : LEAD_SOURCES.map(s=>(
                  <div key={s.label} className="db-source-row">
                    <div className="db-source-dot" style={{background:s.color}}/>
                    <div className="db-source-lbl">{s.label}</div>
                    <div className="db-source-bar-wrap">
                      <div className="db-source-bar-fill" style={{width:(s.val/maxSource*100)+'%',background:s.color}}/>
                    </div>
                    <div className="db-source-pct" style={{color:s.color}}>{s.val}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* KPI summary row */}
          <div className="db-kpi-row-4">
            {KPI4.map(k=>(
              <div key={k.lbl} className="db-kpi4-card" onClick={()=>navigate(k.path)}>
                <div className="db-kpi4-lbl">{k.lbl}</div>
                <div className="db-kpi4-val" style={{color:k.color}}>{k.val}</div>
                <div className="db-kpi4-sub">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Projects + Top clients */}
          <div className="db-bot">
            <div className="db-panel">
              <div className="db-panel-hd">
                <span className="db-panel-title">Live project status</span>
                <span className="db-panel-link" onClick={()=>navigate('/projects')}>View all</span>
              </div>
              {PROJECTS.length === 0 ? <div className="db-empty-state" style={{padding:'12px 0',fontSize:12}}>No active projects</div> : PROJECTS.map(p=>(
                <div key={p.name} className="db-pj-row">
                  <div className="db-pj-left">
                    <div className="db-pj-dot" style={{background:p.color}}/>
                    <div>
                      <div className="db-pj-name">{p.name}</div>
                      <div className="db-pj-status">{p.status}</div>
                    </div>
                  </div>
                  <div className="db-pj-right">
                    <div className="db-pj-bar-wrap"><div className="db-pj-bar-fill" style={{width:p.progress+'%',background:p.color}}/></div>
                    <div className="db-pj-due">{p.due}</div>
                  </div>
                </div>
              ))}
              {PROJECTS.length > 0 && allProjectsOnTrack && <div className="db-all-ok"><span style={{color:'#22C55E'}}>✓</span> All projects on track</div>}
            </div>
            <div className="db-panel">
              <div className="db-panel-hd"><span className="db-panel-title">Top clients by value</span></div>
              {TOP_CLIENTS.length === 0 ? <div className="db-empty-state" style={{padding:'12px 0',fontSize:12}}>No invoiced clients yet</div> : TOP_CLIENTS.map(c=>(
                <div key={c.name} className="db-client-row">
                  <div className="db-client-name">{c.name}</div>
                  <div className="db-client-bar-wrap"><div className="db-client-bar-fill" style={{width:(c.val/maxClient*100)+'%'}}/></div>
                  <div className="db-client-val">{fmtINR(c.val)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent leads + AI */}
          <div className="db-mid">
            <div className="db-panel">
              <div className="db-panel-hd"><span className="db-panel-title">Recent leads</span><span className="db-panel-link" onClick={()=>navigate('/leads')}>View all</span></div>
              {LEADS.length === 0 ? <div className="db-empty-state" style={{padding:'12px 0',fontSize:12}}>No leads yet — add your first lead</div> : LEADS.map(l=>(
                <div key={l.name} className="db-lead-row">
                  <div><div className="db-lead-name">{l.name}</div><div className="db-lead-src">{l.src}</div></div>
                  <span className={`db-badge ${l.cls}`}>{l.badge}</span>
                </div>
              ))}
            </div>
            <div className="db-ai">
              <div className="db-ai-hd"><div className="db-ai-dot"/><span className="db-ai-title">Ryzen AI</span></div>
              {AI_INSIGHTS.map((a,i)=><div key={i} className="db-ai-item">{a}</div>)}
            </div>
          </div>

        </div>
      </div>
      {kpiModal && <KPIModal data={KPI_DETAILS[kpiModal]} theme={theme} onClose={()=>setKpiModal(null)} navigate={navigate}/>}
    </div>
  );
}
